import type { DataSettings, FieldDef } from '@citizen-walk/shared-types';
import {
  BUILTIN_USER_FIELD_KEYS,
  isOtherTextKey,
  normalizeFieldDef,
  OTHER_TEXT_KEY_SUFFIX,
  POST_TYPE_FIELD_KEY,
  SESSIONS_FIELD_KEY,
} from '@citizen-walk/shared-types';

/**
 * DataSettings 編集の pure helper 群 (Sprint BG-DataSettings 3、旧 DataSettingsPage.tsx 踏襲)。
 */

/**
 * UI ラベル (admin の type select 表示用)。
 * 定義本体は shared-types にあり (AI プロンプトの【投稿データ】ブロックと同じ文言を使うため)、
 * ここは従来の import 経路を保つための再エクスポート。
 */
export { FIELD_TYPE_LABELS } from '@citizen-walk/shared-types';

export const FIELD_MODE_LABELS: Record<'required' | 'optional' | 'hidden', string> = {
  required: '必須',
  optional: '任意',
  hidden: '非表示',
};

/** Tailwind 用の mode bar 色クラス。 */
export const FIELD_MODE_COLORS: Record<'required' | 'optional' | 'hidden', string> = {
  required: 'bg-danger-600',
  optional: 'bg-primary-600',
  hidden: 'bg-slate-400',
};

export type FieldSection = 'userFields' | 'postFields';

/**
 * システムが内部キーで扱う項目の規則 (2026-10-07)。「テンプレート由来か」ではなく
 * 「システムがその内部キーで読み書きするか」で決める。ここに無い項目は、テンプレート由来でも
 * 削除・内部キー変更とも自由 (地区ごとにテンプレートを組み替えられる)。
 *
 * - system: 画面の本体が専用の欄で扱う。削除不可・内部キー変更不可 (使わないときは「非表示」)
 *     投稿: 写真 / タイトル / 位置 (投稿の作成に必須) / コメント
 *     参加者: ニックネーム / 参加セッション
 * - storage: 参加者情報の専用欄に保存し、アンケート回答の参加者情報・分析の属性に使う。
 *     削除可・内部キー変更不可 (変えると専用欄ではなく独自項目として保存される)
 * - referenced: 分析ツール・投稿管理・カード印刷などがこの内部キーで値を読む。
 *     削除・内部キー変更とも可だが、それらの表示に出なくなる旨を画面で知らせる
 */
const SYSTEM_FIELD_KEYS: Record<FieldSection, ReadonlySet<string>> = {
  userFields: new Set(['nickname', SESSIONS_FIELD_KEY]),
  postFields: new Set(['photo', 'title', 'location', 'comment']),
};
const STORAGE_FIELD_KEYS: Record<FieldSection, ReadonlySet<string>> = {
  userFields: BUILTIN_USER_FIELD_KEYS,
  postFields: new Set(),
};
const REFERENCED_FIELD_NOTES: Record<FieldSection, Readonly<Record<string, string>>> = {
  userFields: {},
  postFields: {
    HappinessScore: '分析ツールのスコア表示（Happy 軸）・ヒートマップ・並び替え',
    HealthScore: '分析ツールのスコア表示（Healthy 軸）・ヒートマップ・並び替え',
    [POST_TYPE_FIELD_KEY]:
      'AI 対話の文脈（防災の投稿タイプ）・カード印刷のバッジの既定・CSV 取り込み',
    scope: '分析ツールのネットワーク図（主体範囲）',
    duration: '分析ツールのネットワーク図（効果時間）',
  },
};

/** 削除できるか (system 以外は削除できる)。 */
export function isFieldDeletable(section: FieldSection, key: string): boolean {
  return !SYSTEM_FIELD_KEYS[section].has(key);
}

/** 内部キーを変えられるか (system / storage は不可)。 */
export function isFieldKeyFixed(section: FieldSection, key: string): boolean {
  return SYSTEM_FIELD_KEYS[section].has(key) || STORAGE_FIELD_KEYS[section].has(key);
}

/** システムがこの内部キーで値を読んでいる箇所の説明 (無ければ null)。 */
export function fieldKeyReferenceNote(section: FieldSection, key: string): string | null {
  return REFERENCED_FIELD_NOTES[section][key] ?? null;
}

/**
 * 地区の入力項目一覧 (編集・表示用) を作る。投稿画面 (user `resolvePostFields`) と同じ規則:
 *   - 地区に保存値があれば**保存値だけ**を使う
 *   - 保存値が空なら、モードの組込テンプレートを初期値として使う
 * テンプレートにだけある項目は合成しない (2026-10-07)。以前は常にテンプレートを合成していたため、
 * 保存値に無い項目や、組込テンプレートを持たない独自モード (防災テンプレートに fallback) の
 * 防災用項目 (投稿タイプ / 自分の感じ方 / タグ 等) が管理画面に現れ、保存すると地区に書き込まれていた。
 *
 * `builtIn` は内部キーで決める (`isFieldKeyFixed`)。参加者の項目では保存先 (専用欄か独自項目か) を
 * 決める印でもある (`membershipFieldStorageTarget`)。本番・stg の全地区で、従来の印と一致することを
 * 確認済 (2026-10-07)。
 */
export function mergeFields(
  defaults: FieldDef[],
  saved: FieldDef[],
  section: FieldSection,
): FieldDef[] {
  const source = saved.length === 0 ? defaults : saved;
  return source.map((f) => normalizeFieldDef({ ...f, builtIn: isFieldKeyFixed(section, f.key) }));
}

/** 表示順 (displayOrder) の昇順に並べる。未設定は末尾、同じ値は元の並び順を保つ (投稿画面と同じ規則)。 */
export function sortFieldsByDisplayOrder(fields: FieldDef[]): FieldDef[] {
  return fields
    .map((f, i) => ({ f, i }))
    .sort(
      (a, b) =>
        (a.f.displayOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.f.displayOrder ?? Number.MAX_SAFE_INTEGER) || a.i - b.i,
    )
    .map(({ f }) => f);
}

/**
 * FieldDef を Firestore 保存用の plain object に変換する。
 * 旧 v1 `serializeField()` 踏襲: undefined / 不要 default を落として軽量化。
 */
export function serializeField(f: FieldDef): Record<string, unknown> {
  const base: Record<string, unknown> = {
    key: f.key,
    label: f.label,
    type: f.type,
    mode: f.mode,
    options: f.options,
    builtIn: f.builtIn,
    description: f.description,
    definition: f.definition ?? '',
  };
  if (f.optionsMeta) base.optionsMeta = f.optionsMeta;
  if (f.displayStyle) base.displayStyle = f.displayStyle;
  if (typeof f.visible === 'boolean') base.visible = f.visible;
  if (typeof f.displayOrder === 'number' && Number.isFinite(f.displayOrder)) {
    base.displayOrder = f.displayOrder;
  }
  if (typeof f.useAsAnalysisAxis === 'boolean') base.useAsAnalysisAxis = f.useAsAnalysisAxis;
  if (typeof f.otherOption === 'string') base.otherOption = f.otherOption;
  if (typeof f.otherTextInput === 'boolean') base.otherTextInput = f.otherTextInput;
  return base;
}

/** `key` から prefix `custom_` で時刻ベースの新 key を生成。 */
export function makeCustomFieldKey(now: number = Date.now()): string {
  return `custom_${now}`;
}

/** 新規 custom field の初期値 (旧 addField() 相当)。 */
export function makeNewCustomField(now: number = Date.now()): FieldDef {
  return {
    key: makeCustomFieldKey(now),
    label: '新しい項目',
    type: 'text',
    mode: 'optional',
    options: [],
    builtIn: false,
    description: '',
    definition: '',
  };
}

/**
 * userProfileOptions (relationship / attribute) を userFields から逆算。
 * 旧 v1 が `userProfileOptions = { relationships, attributes }` を別途持っていた仕様を踏襲。
 */
export function deriveUserProfileOptions(userFields: FieldDef[]): {
  relationships: string[];
  attributes: string[];
} {
  const rel = userFields.find((f) => f.key === 'relationship');
  const attr = userFields.find((f) => f.key === 'attribute');
  return {
    relationships: rel?.options.map((o) => o.value) ?? [],
    attributes: attr?.options.map((o) => o.value) ?? [],
  };
}

/** DataSettings が「必須 userField を最低 1 つ持つか」検証。旧 v1 保存ガード踏襲。 */
export function countRequiredUserFields(userFields: FieldDef[]): number {
  return userFields.filter((f) => f.mode === 'required').length;
}

/**
 * 内部キーのリネーム検証 (old2 `renameField` 踏襲)。
 *  - 空キー不可
 *  - 同一 section 内での重複不可
 * 戻り値: `{ ok: true }` か `{ ok: false, error }` (i18n 不要の日本語メッセージ)。
 *
 * old2 では `alert()` で通知していたが v2 は呼び出し側で inline 表示する。
 */
export type KeyRenameResult = { ok: true } | { ok: false; error: string };

export function validateKeyRename(
  fields: FieldDef[],
  oldKey: string,
  newKey: string,
): KeyRenameResult {
  if (newKey === oldKey) return { ok: true };
  if (newKey === '') {
    return { ok: false, error: '内部キーは空にできません' };
  }
  if (isOtherTextKey(newKey)) {
    return {
      ok: false,
      error: `内部キーの末尾に "${OTHER_TEXT_KEY_SUFFIX}" は使えません（「その他」の記入内容の保存に使うため）`,
    };
  }
  if (fields.some((f) => f.key === newKey)) {
    return { ok: false, error: `内部キー "${newKey}" は既に使われています` };
  }
  return { ok: true };
}

/**
 * 検証済みのリネームを適用した新しい配列を返す (pure)。検証は呼び出し側 or
 * 本関数内 `validateKeyRename` で実施。検証 NG の場合は元配列をそのまま返す。
 */
export function renameFieldInList(
  fields: FieldDef[],
  oldKey: string,
  newKey: string,
): { fields: FieldDef[]; result: KeyRenameResult } {
  const result = validateKeyRename(fields, oldKey, newKey);
  if (!result.ok || newKey === oldKey) {
    return { fields, result };
  }
  return {
    fields: fields.map((f) => (f.key === oldKey ? { ...f, key: newKey } : f)),
    result,
  };
}

/** options textarea の改行区切り文字列を `FieldOption[]` に変換 (旧 UI 仕様踏襲)。 */
export function parseOptionsLines(text: string): { value: string; label: string }[] {
  const lines = text.split('\n').map((s) => s.trimEnd());
  // value === label で受ける (admin が入力したラベルがそのまま保存値になる、旧仕様)
  return lines
    .filter((line) => line.trim().length > 0)
    .map((line) => ({ value: line, label: line }));
}

/** `FieldOption[]` → 改行区切り文字列 (textarea 表示用)。 */
export function stringifyOptionsLines(options: { value: string; label: string }[]): string {
  return options.map((o) => o.label).join('\n');
}
